// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import React, { useMemo, useState } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";
import { SlateFilmGrid } from "./youtube/slate";
import { DirectionFilmGrid, DIRECTION_COUNT } from "./youtube/directions";
import { StoryboardViewer } from "./youtube/StoryboardViewer";

// ─────────────────────────────────────────────────────────────────────────────
// YOUTUBE BOARD v5 — Remy (Head of YouTube, Team Reel).
//
// CONSOLIDATED 2026-08-13, at Erez's instruction: "go to your board and
// consolidate it into a few sections… I still don't see any examples of actual
// videos that we can create."
//
// Both halves of that sentence were the same problem. The board had ELEVEN
// sections, nine of which were walls of research cards, and the films sat in
// the eleventh — at the far end of a 24,000px canvas. The videos were live on
// R2 the whole time and loading fine; he simply never reached them. So:
//
//   ELEVEN SECTIONS → FOUR, and THE GALLERY COMES FIRST.
//
// Nothing was deleted. Every research card wall was rewritten as a proper KB
// document and is presented here through <DocumentFrame> — which is the board
// law anyway ("if you READ it, it is a DOCUMENT; if you SCAN it, it stays a
// BOX"). The documents are data: editing them needs no deploy.
//
//   kb-doc/projects/youtube-content/the-plan            ← The Channels + gates
//   kb-doc/projects/youtube-content/channel-research    ← Geopolitics + AI Discoveries + Human Nature
//   kb-doc/projects/youtube-content/monetization        ← Monetization
//   kb-doc/projects/youtube-content/what-we-learned     ← Key Insights + Scott Smith
//   kb-doc/projects/youtube-content/visual-directions   ← why the first ten failed, and the six that replace them
// ─────────────────────────────────────────────────────────────────────────────

const T = { ink: "#1C1917", ink2: "#57534E", line: "#EADDD0", white: "#FFFFFF", canvas: "#E5E5E5", panel: "#F2EDE8" };
const FONT = "Inter, system-ui, -apple-system, sans-serif";
const PETROL = "#2B5560";
const SAGE = "#5E7D5E";
const SN = "0 12px 30px rgba(0,0,0,0.08)";

const R2_LAB = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/content-lab";

// ─────────────────────────────────────────────────────────────────────
// The older Content Lab samples. KEPT — a board is cumulative and these
// are the record of the production-method tests. They are thin technique
// probes, not finished films; the finished work is in The Gallery.
// ─────────────────────────────────────────────────────────────────────
const VIDEO_METHODS = [
  { id: "01", name: "Remotion Slide Deck", desc: "React-powered video. Full control over every frame.", r2File: "01-remotion-slides-sample-remotion-slides.mp4" },
  { id: "02", name: "FFmpeg Slideshow", desc: "Image sequence to video. Batch crossfade. Universal fallback.", r2File: "02-ffmpeg-slideshow-sample-ffmpeg-slideshow.mp4" },
  { id: "03", name: "Nano Banana Essay", desc: "Ownable NB art into video. Our visual moat.", r2File: "03-nb-essay-sample-nb-essay.mp4" },
  { id: "04", name: "Ken Burns Maps", desc: "Animated map zooms for geopolitics.", r2File: "04-ken-burns-sample-ken-burns.mp4" },
  { id: "05", name: "Kinetic Typography", desc: "Animated text synced to narration.", r2File: "05-kinetic-type-sample-kinetic-type.mp4" },
  { id: "06", name: "Text Overlay + VO", desc: "Simple text + calm VO. Fastest pipeline.", r2File: "06-text-overlay-sample-text-overlay.mp4" },
  { id: "07", name: "Split Screen", desc: "Side-by-side comparisons.", r2File: "07-split-screen-sample-split-screen.mp4" },
  { id: "08", name: "Tutorial Screencast", desc: "Screen recording with narration.", r2File: "08-tutorial-sample-tutorial.mp4" },
  { id: "09", name: "Data Viz", desc: "Animated charts and data.", r2File: "09-data-viz-sample-data-viz.mp4" },
  { id: "10", name: "Short Explainer", desc: "60-90s dense explainer. Subscriber velocity.", r2File: "10-short-sample-short.mp4" },
];

const CHANNEL_DEMOS = [
  { id: "01", name: "Stacked", desc: "5 AI writing tools tested for 30 days.", r2File: "channel-demos/stacked.mp4" },
  { id: "02", name: "The World Map", desc: "5 conflicts defining 2026.", r2File: "channel-demos/world-map.mp4" },
  { id: "03", name: "AI Discoveries", desc: "AI discovering drugs, weather, materials, languages.", r2File: "channel-demos/ai-discoveries.mp4" },
  { id: "04", name: "Human Nature", desc: "Ancient software running your brain.", r2File: "channel-demos/human-nature.mp4" },
];

function VideoCard({ data }: { data: { id: string; name: string; desc: string; r2File: string } }) {
  const [showVideo, setShowVideo] = useState(false);
  const vUrl = R2_LAB + "/" + data.r2File;

  if (showVideo) {
    return (
      <div className="nopan nodrag" style={{ pointerEvents: "auto", fontFamily: FONT, width: 380, background: T.white, border: `1px solid ${T.line}`, borderRadius: 14, boxShadow: SN, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <video
          className="nopan nodrag"
          src={vUrl}
          controls
          autoPlay
          style={{ width: "100%", display: "block", background: "#000", pointerEvents: "auto" }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        />
        <div style={{ padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", pointerEvents: "none" }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{data.name}</span>
          <button onClick={(e) => { e.stopPropagation(); setShowVideo(false); }} className="nopan nodrag"
            style={{ pointerEvents: "auto", cursor: "pointer", fontSize: 10, color: T.ink2, border: "none", background: T.panel, borderRadius: 6, padding: "4px 8px" }}>× Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="nopan nodrag" onClick={(e) => { e.stopPropagation(); setShowVideo(true); }}
      style={{ pointerEvents: "auto", cursor: "pointer", fontFamily: FONT, width: 380, background: T.white, border: `1px solid ${T.line}`, borderRadius: 14, boxShadow: SN, overflow: "hidden" }}>
      <div style={{ width: "100%", aspectRatio: "16/9", background: "#1a1a1a", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(255,255,255,0.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 24, color: "#1a1a1a", marginLeft: 3 }}>▶</span>
        </div>
      </div>
      <div style={{ padding: "10px 14px", pointerEvents: "none" }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: T.ink }}>{data.name}</span>
        <span style={{ fontSize: 10.5, color: T.ink2, lineHeight: 1.35, display: "block", marginTop: 2 }}>{data.desc}</span>
      </div>
    </div>
  );
}

/** A shelf label INSIDE a section — the gallery holds two generations of film
 *  and they must not read as one undifferentiated pile. Not a second section
 *  title: the SectionBox keeps its single black pill. */
function Shelf({ title, note, tone = PETROL }: { title: string; note: string; tone?: string }) {
  return (
    <div style={{ fontFamily: FONT, display: "flex", flexDirection: "column", gap: 4, marginBottom: 4 }}>
      <span style={{ fontSize: 17, fontWeight: 800, color: tone, letterSpacing: "-0.01em" }}>{title}</span>
      <span style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.45, maxWidth: 1180 }}>{note}</span>
    </div>
  );
}

/** Live state only — the one thing board law says stays board-native. */
function GateCard({ n, label, body, state }: { n: string; label: string; body: string; state: "done" | "open" | "waiting" }) {
  const tone = state === "done" ? SAGE : state === "open" ? PETROL : "#9A8FA0";
  const dot = state === "done" ? "🟢" : state === "open" ? "🔵" : "⚪";
  return (
    <div className="nopan nodrag" style={{ pointerEvents: "auto", fontFamily: FONT, width: 360, background: T.white, borderRadius: 12, padding: 16, border: `${state === "open" ? 2 : 1}px solid ${state === "open" ? tone : T.line}`, boxShadow: SN, display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 800, color: tone, letterSpacing: "0.05em" }}>{dot} GATE {n} — {label}</span>
      <span style={{ fontSize: 12, color: T.ink2, lineHeight: 1.45 }}>{body}</span>
    </div>
  );
}

function GridNode() {
  return (
    <SectionGrid>
      {/* ============================================================== */}
      {/* 1 · THE GALLERY — the films. FIRST, because this is what Erez   */}
      {/*     asked to see and could not find.                            */}
      {/* ============================================================== */}
      <SectionBox title="The Gallery" layout="custom">
        <div style={{ display: "flex", flexDirection: "column", gap: 44 }}>
          <Shelf
            title={`Travel pilot 1 + the six directions${DIRECTION_COUNT ? "" : " (rendering)"}`}
            note="The last film in this shelf — The Road That Killed 300 People A Year — is the first video of the channel Erez approved on 13 Aug: faceless place-documentary, one place per film, a story you cannot see in the photograph. It is also the first built to the measured spec: 2560×1440, audio mastered to −14 LUFS / −1 dBTP, and a 60-second “if you want to go” chapter at the end, which is the only moment a documentary viewer has buying intent and therefore the only honest place for an affiliate link. It fails one gate on purpose-to-be-visible: dynamics, LRA 1.10 against a 6 target, because there is no music bed under the narration yet. The six before it are the format tests that led here — real licensed footage filling the frame, graphics riding on top, no AI-generated video, no spend."
          />
          <DirectionFilmGrid cols={3} />

          <div style={{ height: 1, background: "#BEBEBE", opacity: 0.8, margin: "8px 0" }} />

          <Shelf
            title="The first slate — type-led (superseded)"
            note="Ten format tests, kept as the record. Erez's verdict on 13 Aug: “very static, they feel more like a pitch deck than a video.” He was right — every one is type on a flat colour field, and the only motion is text arriving. They stay on the board because a board is cumulative, and because the comparison is the clearest argument for the six above."
            tone="#8A8378"
          />
          <SlateFilmGrid cols={5} />
        </div>
      </SectionBox>

      {/* ============================================================== */}
      {/* 2 · THE PLAN — one document + the live gates                    */}
      {/* ============================================================== */}
      <SectionBox title="The Plan" layout="custom">
        <div style={{ display: "flex", gap: 44, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/the-plan"
            label="The Plan — four channels, one pipeline"
            width={900}
            height={1180}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <GateCard n="1" label="Approve the plan" state="done"
              body="Four channels locked, Human Nature confirmed as channel 4. Done." />
            <GateCard n="2" label="The Google account" state="open"
              body="Erez creates one Google account (~10 min, needs phone verification), then all four Brand channels get built. This blocks any publish." />
            <GateCard n="3" label="First video" state="waiting"
              body="First Stacked episode produced, scored by the LLM judge against the benchmark, Erez approves the publish." />
            <div className="nopan nodrag" style={{ pointerEvents: "auto", fontFamily: FONT, width: 360, background: T.panel, borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: T.ink, letterSpacing: "0.05em" }}>🔵 OPEN DECISION</span>
              <span style={{ fontSize: 12, color: T.ink2, lineHeight: 1.45 }}>
                <b>Which directions do we pursue?</b> Watch the six in The Gallery and pick. Everything downstream — the production pipeline, the weekly cadence, which channel launches first — waits on that call.
              </span>
              <span style={{ fontSize: 12, color: T.ink2, lineHeight: 1.45, marginTop: 4 }}>
                <b>And the money model:</b> researched 13 Aug — the memo is in The Money Model below, with a ranked recommendation. Erez's call.
              </span>
            </div>
          </div>
        </div>
      </SectionBox>

      {/* ============================================================== */}
      {/* 2.5 · STORYBOARDS — scripts first, per Erez 15 Aug              */}
      {/* ============================================================== */}
      <SectionBox title="Storyboards" layout="custom">
        <StoryboardViewer />
      </SectionBox>

      {/* ============================================================== */}
      {/* 2.55 · THE SCRIPT — episode 1, both lengths, 15 Aug             */}
      {/*        The Gate A 90 seconds was held off the board while it    */}
      {/*        waited on a verdict. Now that the full 20-minute script  */}
      {/*        exists, hiding the thing he is being asked to judge is   */}
      {/*        worse than showing it — the board IS the review surface. */}
      {/* ============================================================== */}
      <SectionBox title="The Script" layout="custom">
        <div style={{ display: "flex", gap: 44, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/episode-1-full-script"
            label="Episode 1 — The Bill Nobody Signed: the full 20-minute script, seven acts, every figure sourced"
            width={1180}
            height={1600}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/gate-a-test-script"
            label="Gate A — the opening 90 seconds, which is now this episode's act 1. Awaiting your verdict"
            width={1180}
            height={1600}
          />
          {/* The biggest untested risk, turned into measured numbers: our
              narrator runs at 207 wpm not 140, which reframes the pacing rule
              as a silence budget rather than a diction target. */}
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/pipeline-at-length"
            label="The pipeline at full length — 207 wpm not 140, 7.5× realtime render, and why the film can never be a Telegram file"
            width={1180}
            height={1600}
          />
        </div>
      </SectionBox>

      {/* ============================================================== */}
      {/* 2.6 · THE BUSINESS PLAN — the two channels Erez chose, 15 Aug   */}
      {/*        Concept, people, money, costs, gates. The spine.         */}
      {/* ============================================================== */}
      <SectionBox title="The Business Plan" layout="custom">
        <DocumentFrame
          slug="kb-doc/projects/youtube-content/business-plan"
          label="Follow The Money & The Map — concept, audience, monetization, costs and the gates that stop us"
          width={1180}
          height={1600}
        />
      </SectionBox>

      {/* ============================================================== */}
      {/* 2.7 · THE COMPETITION — 16 channels on real view data, 15 Aug   */}
      {/*        Erez picked the two categories; this is round 1.         */}
      {/*        Rounds 2 (hooks/sponsors) + 3 (story shape) added below  */}
      {/*        on the same day — the study is cumulative, one row.      */}
      {/* ============================================================== */}
      <SectionBox title="The Competition" layout="custom">
        <div style={{ display: "flex", gap: 44, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/competitor-study"
            label="Round 1 — 16 channels measured: the top four in each category, and the three findings that change our plan"
            width={1180}
            height={1500}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/coldfusion-decoded"
            label="ColdFusion decoded — 250 videos: it is a financial-crime channel, and AI adds nothing to it"
            width={1180}
            height={1500}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/hooks-and-sponsors"
            label="Round 2 — 33 transcripts: not one opens with context, and the biggest channels barely take sponsors"
            width={1180}
            height={1500}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/story-shape"
            label="Round 3 — 18 long-form winners, 74,000 words: the six-act shape, and why we should narrate at 140 wpm, not 175"
            width={1180}
            height={1500}
          />
        </div>
      </SectionBox>

      {/* ============================================================== */}
      {/* 3 · THE MONEY MODEL — the answer to the open decision above     */}
      {/* ============================================================== */}
      <SectionBox title="The Money Model" layout="custom">
        <div style={{ display: "flex", gap: 44, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/money-model"
            label="How this makes money — four models ranked, and a straight answer on politics"
            width={980}
            height={1360}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/niche-scan"
            label="The numbers & the plan — six niches measured, two channels proposed"
            width={980}
            height={1360}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/quality-bar"
            label="The quality bar — our films measured, and the rubric nothing ships without"
            width={980}
            height={1360}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/how-this-is-done"
            label="How this is actually done — the craft standards, the tooling landscape, and what we got wrong"
            width={980}
            height={1360}
          />
        </div>
      </SectionBox>

      {/* ============================================================== */}
      {/* 4 · THE RESEARCH — four card walls, now four documents          */}
      {/* ============================================================== */}
      <SectionBox title="The Research" layout="custom">
        <div style={{ display: "flex", gap: 40, alignItems: "flex-start" }}>
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/visual-directions"
            label="Visual directions & the footage stack"
            width={860}
            height={1180}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/channel-research"
            label="Channel research — the reference wall"
            width={860}
            height={1180}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/monetization"
            label="Monetization"
            width={860}
            height={1180}
          />
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/what-we-learned"
            label="What we learned + Scott Smith teardown"
            width={860}
            height={1180}
          />
          {/* Closes the act-5 hole that round 3 opened: the named humans are
              already on the public record, so the six-act spec is executable
              without recording anyone. Tested against the live PJM story. */}
          <DocumentFrame
            slug="kb-doc/projects/youtube-content/act-five"
            label="Act 5 — where the named humans come from, without recording anyone"
            width={860}
            height={1180}
          />
        </div>
      </SectionBox>

      {/* ============================================================== */}
      {/* 4 · CONTENT LAB — the early technique probes. Kept.             */}
      {/* ============================================================== */}
      <SectionBox title="Content Lab" layout="custom">
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          <Shelf
            title="Production-method probes"
            note="Ten technique tests across the production methods, plus four first-look channel demos. These are short probes that proved a pipeline works — not finished films. Kept as the record of what was tried."
            tone="#8A8378"
          />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, max-content)", columnGap: 32, rowGap: 32 }}>
            {VIDEO_METHODS.map((m) => <VideoCard key={m.id} data={m} />)}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, max-content)", columnGap: 32, rowGap: 32 }}>
            {CHANNEL_DEMOS.map((c) => <VideoCard key={c.id} data={c} />)}
          </div>
        </div>
      </SectionBox>
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = { grid: GridNode as unknown as NodeTypes[string] };
function buildNodes(): Node[] {
  return [{ id: "board-grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, selectable: false, data: {} }];
}

// ── Page ─────────────────────────────────────────────────────────────────────
export function YouTubeBoardPage() {
  const nodes = useMemo<Node[]>(() => buildNodes(), []);

  return (
    <div className="yt-board" style={{ position: "absolute", inset: 0, background: T.canvas }}>
      <style>{`
        .yt-board .react-flow__pane { cursor: grab; }
        .yt-board .react-flow__pane.dragging { cursor: grabbing; }
        .yt-board .react-flow__node { cursor: grab; }
        .yt-board .react-flow__node img { pointer-events: none; }
      `}</style>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.06} />
    </div>
  );
}
